import type { VideoFile } from "@brookbush/types";

import { PortableText, type PortableTextComponents } from "@portabletext/react";
import type { PortableTextBlock } from "@portabletext/types";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { memo, useMemo } from "react";

import InlineCTA, { type InlineCTAProps } from "./InlineCTA";
import VideoPlayer from "./VideoPlayer";
import { portableTextHeadingId } from "~/lib/content/portableTextHeadingId";

interface TextBlockProps {
	value: PortableTextBlock[];
	expanded?: boolean;
	className?: string;
	isBiblio?: boolean;
	isArticle?: boolean;
	isExamQuestion?: boolean;
}

const TextBlock = ({
	value,
	className,
	isBiblio,
	isExamQuestion,
	expanded = true,
}: TextBlockProps) => {
	const textBlockClass = useMemo(() => {
		return clsx(`text-block ${className}`, {
			"blur-bottom": !expanded,
		});
	}, [className, expanded]);

	const components = useMemo((): PortableTextComponents => {
		return {
			types: {
				"mux.video": ({ value }) => {
					const { thumbnailUrl, blurDataURL, playbackId, duration } =
						value as VideoFile;

					return (
						<div className="z-10 mx-auto laptop:my-2 flex aspect-[16/9] max-w-3xl items-center laptop:rounded-2xl rounded-xl laptop:border laptop:bg-black pt-0 shadow-xl">
							<VideoPlayer
								playbackId={playbackId}
								thumbnailUrl={thumbnailUrl}
								blurDataURL={blurDataURL}
								duration={duration}
							/>
						</div>
					);
				},

				image: ({ value: image }) => {
					return (
						<div
							className={clsx(
								"mx-auto my-4 flex w-full flex-col items-center overflow-hidden rounded-xl border bg-white font-medium text-base shadow-black/5 shadow-xl sm:my-8 dark:shadow-black/10",
								{ "max-w-max": !isExamQuestion },
							)}
						>
							<div className="relative aspect-square w-full sm:w-[25rem]">
								<Image
									src={image.url}
									fill
									className="object-contain"
									alt={image.alt ?? ""}
								/>
							</div>

							{image.alt && (
								<span className="max-w-lg border-t px-4 py-2.5 font-normal text-black sm:text-lg dark:border-slate-300">
									<span className="font-semibold">Caption:</span> {image.alt}
								</span>
							)}
						</div>
					);
				},

				code: ({ value: code }) => {
					return (
						<div
							className="w-full laptop:max-w-screen-md phablet:max-w-2xl overflow-auto"
							dangerouslySetInnerHTML={{
								__html: code.code,
							}}
						/>
					);
				},
				cta: ({ value }) => {
					return <InlineCTA {...(value as InlineCTAProps)} />;
				},
			},

			marks: {
				link: ({ children, value }) => {
					const rel = !value?.href?.startsWith("/")
						? "noreferrer noopener"
						: "";
					return (
						<Link
							href={value?.href || ""}
							target="_blank"
							className="font-medium"
							rel={rel}
						>
							{children}{" "}
						</Link>
					);
				},
			},

			block: {
				h1: ({ children }) => (
					<h1 id={portableTextHeadingId(children)}>{children}</h1>
				),
				h2: ({ children }) => (
					<h2 id={portableTextHeadingId(children)}>{children}</h2>
				),
				h3: ({ children }) => (
					<h3 id={portableTextHeadingId(children)}>{children}</h3>
				),
			},
			list: isBiblio
				? {
						bullet: ({ children }) => <ul className="biblio-ul">{children}</ul>,
						number: ({ children }) => <ol className="biblio-ol">{children}</ol>,
					}
				: undefined,

			listItem: isBiblio
				? {
						bullet: ({ children }) => (
							<li className="biblio-li">
								<span>{children}</span>
							</li>
						),
						number: ({ children }) => (
							<li className="biblio-li">
								<span>{children}</span>
							</li>
						),
					}
				: undefined,
		};
	}, [isBiblio, isExamQuestion]);

	return (
		<div className={textBlockClass}>
			<PortableText value={value} components={components} />
		</div>
	);
};

export default memo(TextBlock);
